import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import './DailyPanchang.css';
import { LanguageContext } from "../context/LanguageContext";
import html2canvas from 'html2canvas';
import headerImage from "../assets/Website Desing_header.png";
import footerImage from "../assets/Website Desing_Footer-2.png";
import { IoShareSocial, IoLogoWhatsapp, IoDownload, IoCheckmarkCircle, IoClose, IoImage } from "react-icons/io5";

export default function DailyPanchang() {
  const [isOpen, setIsOpen] = useState(false);
  const [panchang, setPanchang] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Share states
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareOptionsOpen, setShareOptionsOpen] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null); // { blob, url }
  const [toastMessage, setToastMessage] = useState(null);

  const { lang } = useContext(LanguageContext);
  const contentRef = useRef(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const toDevanagariNumber = (num) => {
    if (typeof num !== 'string' && typeof num !== 'number') return '';
    const d = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return num.toString().split('').map(x => d[x] || x).join('');
  };

  const fromDevanagariNumber = (str) => {
    if (typeof str !== 'string') return str;
    const d = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    let s = str;
    for (let i = 0; i < 10; i++) s = s.replace(new RegExp(d[i], 'g'), String(i));
    return s;
  };

  const getDisplayDate = () => {
    const date = new Date();
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const monthNames = {
      en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      mr: ['जाने', 'फेब्रु', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टें', 'ऑक्टो', 'नोव्हें', 'डिसें']
    };
    return { day, month: { en: monthNames.en[monthIndex], mr: monthNames.mr[monthIndex] } };
  };

  const dateInfo = getDisplayDate();

  const labels = {
    date: { en: 'Date', mr: 'दिनांक' },
    savastar: { en: 'Samvatsar', mr: 'संवत्सर' },
    shake: { en: 'Shak', mr: 'शके' },
    vaar: { en: 'Day', mr: 'वार' },
    ayan: { en: 'Ayan', mr: 'आयन' },
    ritu: { en: 'Season', mr: 'ऋतू' },
    maas: { en: 'Month', mr: 'मास' },
    paksha: { en: 'Paksha', mr: 'पक्ष' },
    tithi: { en: 'Tithi', mr: 'तिथी' },
    nakshatra: { en: 'Nakshatra', mr: 'नक्षत्र' },
    yog: { en: 'Yoga', mr: 'योग' },
    karan: { en: 'Karan', mr: 'करण' },
    chandra_rashi: { en: 'Moon Sign', mr: 'चंद्र राशी' },
    surya_rashi: { en: 'Sun Sign', mr: 'सूर्य राशी' },
    sunrise: { en: 'Sunrise', mr: 'सूर्योदय' },
    sunset: { en: 'Sunset', mr: 'सूर्यास्त' },
    moonrise: { en: 'Moonrise', mr: 'चंद्रोदय' },
    moonset: { en: 'Moonset', mr: 'चंद्रास्त' },
    rahukaal: { en: 'Rahukaal', mr: 'राहुकाल' },
    guruwani: { en: "Guru's Message", mr: 'गुरुवाणी' },
    noData: { en: 'Data not available for today', mr: 'आजसाठी डेटा उपलब्ध नाही' }
  };

  const formatValue = (val) => {
    if (!val) return '';
    return val;
  };

  const formatTime12Hr = (time24) => {
    if (!time24) return '';
    const parts = time24.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const seconds = parts[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const formatWithEndTime = (value, endTime) => {
    if (!value) return '';
    return `${value} ${endTime} पर्यंत`;
  };

  const formatRahukaal = (marathiText) => {
    if (!marathiText) return '';
    let englishText = marathiText;
    const timeOfDay = { 'दुपारी': 'Afternoon', 'सकाळी': 'Morning', 'सायं.': 'Evening' };
    for (const [mr, en] of Object.entries(timeOfDay)) {
      englishText = englishText.replace(mr, en);
    }
    englishText = englishText.replace('ते', 'to');
    englishText = fromDevanagariNumber(englishText);
    return `${englishText} (${marathiText})`;
  };

  const formatDualLanguageDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    const [year, month, day] = parts;
    const enDate = `${day}-${month}-${year}`;
    const mrDate = `${toDevanagariNumber(day)}-${toDevanagariNumber(month)}-${toDevanagariNumber(year)}`;
    return `${enDate} (${mrDate})`;
  };

  // Generate Panchang image with clean offscreen layout and 2 center Dindoripranit.org watermarks
  const generatePanchangImage = useCallback(async (panchangData) => {
    if (!panchangData) return null;

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.width = '620px';
    container.style.background = 'linear-gradient(145deg, #fffbf5 0%, #fff8ef 100%)';
    container.style.padding = '28px';
    container.style.borderRadius = '24px';
    container.style.boxSizing = 'border-box';
    container.style.fontFamily = "'Mukta', 'Poppins', sans-serif";
    container.style.zIndex = '-9999';

    const row = (labelObj, valueStr) => `
      <tr style="background: rgba(255, 255, 255, 0.94); border-radius: 10px; box-shadow: 0 2px 6px rgba(0,0,0,0.05);">
        <td style="padding: 11px 16px; font-weight: 700; color: #ff6b35; width: 40%; font-size: 15px; border-right: 2px solid rgba(255,153,51,0.2); border-radius: 10px 0 0 10px; background: rgba(255,153,51,0.08);">
          ${labelObj.en} (${labelObj.mr})
        </td>
        <td style="padding: 11px 16px; color: #2d3748; font-weight: 600; font-size: 15px; border-radius: 0 10px 10px 0;">
          ${valueStr || ''}
        </td>
      </tr>
      <tr style="height: 6px;"></tr>
    `;

    const loadImage = (src) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });

    await loadImage(headerImage);
    await loadImage(footerImage);

    container.innerHTML = `
      <div style="position: relative; overflow: hidden; border-radius: 20px; padding: 4px;">
        <div style="margin-bottom: 18px;">
          <img src="${headerImage}" alt="Dindori Pranit Header" style="width: 100%; height: auto; display: block; border-radius: 18px; object-fit: cover;" />
        </div>

        <!-- Center Watermark 1 (DOM Layer) -->
        <div style="position: absolute; top: 35%; left: 50%; transform: translate(-50%, -50%) rotate(-22deg); font-size: 42px; font-weight: 900; color: rgba(255, 107, 53, 0.25); text-align: center; pointer-events: none; width: 100%; font-family: 'Poppins', sans-serif; letter-spacing: 2px; z-index: 10; text-transform: uppercase;">
          Dindoripranit.org
        </div>
        <!-- Center Watermark 2 (DOM Layer) -->
        <div style="position: absolute; top: 68%; left: 50%; transform: translate(-50%, -50%) rotate(-22deg); font-size: 42px; font-weight: 900; color: rgba(255, 107, 53, 0.25); text-align: center; pointer-events: none; width: 100%; font-family: 'Poppins', sans-serif; letter-spacing: 2px; z-index: 10; text-transform: uppercase;">
          Dindoripranit.org
        </div>

        <table style="width: 100%; border-collapse: collapse; position: relative; z-index: 2;">
          <tbody>
            ${row(labels.date, formatDualLanguageDate(panchangData.date))}
            ${row(labels.savastar, formatValue(panchangData.savastar))}
            ${row(labels.shake, formatValue(panchangData.shake))}
            ${row(labels.vaar, formatValue(panchangData.vaar))}
            ${row(labels.ayan, formatValue(panchangData.ayan))}
            ${row(labels.ritu, formatValue(panchangData.ritu))}
            ${row(labels.maas, formatValue(panchangData.maas))}
            ${row(labels.paksha, formatValue(panchangData.paksha))}
            ${row(labels.tithi, formatWithEndTime(panchangData.tithi, panchangData.tithiEndTime))}
            ${row(labels.nakshatra, formatWithEndTime(panchangData.nakshatra, panchangData.nakshatraEndTime))}
            ${row(labels.yog, formatWithEndTime(panchangData.yog, panchangData.yogEndTime))}
            ${row(labels.karan, formatWithEndTime(panchangData.karan, panchangData.karanEndTime))}
            ${row(labels.chandra_rashi, formatValue(panchangData.chandra_Rashi))}
            ${row(labels.surya_rashi, formatValue(panchangData.surya_Rashi))}
            ${row(labels.sunrise, formatTime12Hr(panchangData.sunrise))}
            ${row(labels.sunset, formatTime12Hr(panchangData.sunset))}
            ${row(labels.moonrise, formatTime12Hr(panchangData.moonrise))}
            ${row(labels.moonset, formatTime12Hr(panchangData.moonset))}
            ${row(labels.rahukaal, formatRahukaal(panchangData.rahukaal))}
            ${panchangData.guruwani ? `
              <tr style="background: linear-gradient(135deg, rgba(255, 153, 51, 0.15), rgba(255, 107, 53, 0.08)); border: 2px solid rgba(255, 153, 51, 0.4); border-radius: 12px;">
                <td colspan="2" style="padding: 16px 18px; color: #2d3748; font-size: 15px; border-radius: 12px; line-height: 1.6;">
                  <strong style="color: #ff6b35; font-size: 16px;">${labels.guruwani.en} (${labels.guruwani.mr}):</strong><br />
                  ${formatValue(panchangData.guruwani)}
                </td>
              </tr>
            ` : ''}
          </tbody>
        </table>

        <div style="text-align: center; margin-top: 18px; padding-top: 12px; border-top: 2px dashed rgba(255, 153, 51, 0.3); font-size: 13px; color: #ff6b35; font-weight: 700; font-family: 'Poppins', sans-serif; position: relative; z-index: 2;">
          ✨ Dindori Pranit Official Daily Panchang • www.dindoripranit.org ✨
        </div>
        <div style="margin-top: 18px;">
          <img src="${footerImage}" alt="Dindori Pranit Footer" style="width: 100%; height: auto; display: block; border-radius: 18px; object-fit: cover;" />
        </div>
      </div>
    `;

    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        backgroundColor: '#fffbf5',
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      document.body.removeChild(container);

      // Render 2 Large Canvas 2D Center Watermarks
      const ctx = canvas.getContext('2d');
      const w = canvas.width, h = canvas.height;

      ctx.save();
      const fontSize = Math.min(w, h) * 0.065;
      ctx.font = `bold ${fontSize}px 'Poppins', Arial, sans-serif`;
      ctx.fillStyle = 'rgba(255, 107, 53, 0.22)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Watermark 1 (Upper Center)
      ctx.save();
      ctx.translate(w / 2, h * 0.35);
      ctx.rotate(-Math.PI / 9);
      ctx.fillText('Dindoripranit.org', 0, 0);
      ctx.restore();

      // Watermark 2 (Lower Center)
      ctx.save();
      ctx.translate(w / 2, h * 0.68);
      ctx.rotate(-Math.PI / 9);
      ctx.fillText('Dindoripranit.org', 0, 0);
      ctx.restore();

      ctx.restore();

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(null); return; }
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        }, 'image/png');
      });
    } catch (e) {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    const fetchPanchang = async () => {
      setLoading(true);
      try {
        const res = await fetch('https://itv.dindoripranit.org/api/v1/panchang/today', { headers: { 'accept': '*/*' } });
        const json = await res.json();
        if (json.isSuccess && json.value) {
          setPanchang(json.value);
          // Pre-generate image in background with 2 center watermarks for instant sharing
          generatePanchangImage(json.value).then((imgData) => {
            if (imgData) setGeneratedImage(imgData);
          });
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPanchang();
  }, [generatePanchangImage]);

  const generateWithTimeout = async (data, timeoutMs = 10000) => {
    return Promise.race([
      generatePanchangImage(data),
      new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs))
    ]);
  };

  const handleShareClick = async () => {
    if (!panchang) return;

    setIsGenerating(true);
    try {
      if (!generatedImage) {
        const imgData = await generateWithTimeout(panchang, 10000);
        if (imgData) {
          setGeneratedImage(imgData);
        }
      }
    } catch (e) {
    } finally {
      setIsGenerating(false);
      setShareOptionsOpen(true);
    }
  };

  const handleShareImageNative = async () => {
    let imgData = generatedImage;
    if (!imgData && panchang) {
      setIsGenerating(true);
      try {
        imgData = await generateWithTimeout(panchang, 10000);
        if (imgData) setGeneratedImage(imgData);
      } catch (e) {
      } finally {
        setIsGenerating(false);
      }
    }

    const shareText = lang === 'en'
      ? "Check out today's Daily Panchang at Dindori Pranit:\nhttps://dindoripranit.org"
      : "दिंडोरी प्रणीत आजचे दैनिक पंचांग पहा:\nhttps://dindoripranit.org";

    if (imgData && imgData.blob && navigator.share && navigator.canShare) {
      const file = new File([imgData.blob], 'daily-panchang.png', { type: 'image/png' });
      const payload = { title: 'Daily Panchang - Dindori Pranit', text: shareText, files: [file] };

      if (navigator.canShare(payload)) {
        try {
          await navigator.share(payload);
          return;
        } catch (e) {
          return;
        }
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Daily Panchang - Dindori Pranit', text: shareText, url: 'https://dindoripranit.org' });
      } catch (e) {}
    } else {
      handleDownloadImage();
    }
  };

  // WhatsApp share: Automatically takes Panchang Image + Text combined
  const handleWhatsAppShare = async () => {
    let imgData = generatedImage;
    if (!imgData && panchang) {
      setIsGenerating(true);
      try {
        imgData = await generateWithTimeout(panchang, 10000);
        if (imgData) setGeneratedImage(imgData);
      } catch (e) {
      } finally {
        setIsGenerating(false);
      }
    }

    const shareText = lang === 'en'
      ? "Check out today's Daily Panchang at Dindori Pranit:\nhttps://dindoripranit.org"
      : "दिंडोरी प्रणीत आजचे दैनिक पंचांग पहा:\nhttps://dindoripranit.org";

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
    const whatsappWebUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

    const tryOpenWhatsapp = (url) => {
      try {
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.focus?.();
          return true;
        }
      } catch (e) {
      }
      return false;
    };

    if (imgData && imgData.blob && navigator.share && navigator.canShare) {
      const file = new File([imgData.blob], 'daily-panchang.png', { type: 'image/png' });
      const payload = { title: 'Daily Panchang - Dindori Pranit', text: shareText, files: [file] };

      if (navigator.canShare(payload)) {
        try {
          await navigator.share(payload);
          return;
        } catch (e) {
          // fall through to WhatsApp fallback
        }
      }
    }

    const imageDownloaded = Boolean(imgData && imgData.url);
    if (imageDownloaded) {
      const a = document.createElement('a');
      a.href = imgData.url;
      a.download = `daily-panchang-${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    let opened = false;
    if (isMobile) {
      opened = tryOpenWhatsapp(whatsappAppUrl);
      if (!opened) {
        opened = tryOpenWhatsapp(whatsappWebUrl);
      }
    } else {
      opened = tryOpenWhatsapp(whatsappWebUrl);
    }

    if (opened) {
      showToast(lang === 'en' ? 'WhatsApp opened!' : 'व्हॉट्सॲप उघडले!');
    } else if (imageDownloaded) {
      showToast(lang === 'en' ? 'Image downloaded. Open WhatsApp to share.' : 'इमेज डाऊनलोड झाली. शेअर करण्यासाठी व्हॉट्सॲप उघडा.');
    } else {
      showToast(lang === 'en' ? 'Unable to open WhatsApp.' : 'व्हॉट्सॲप उघडता आले नाही.');
    }
  };

  const handleDownloadImage = async () => {
    let imgData = generatedImage;
    if (!imgData && panchang) {
      setIsGenerating(true);
      try {
        imgData = await generateWithTimeout(panchang, 10000);
        if (imgData) setGeneratedImage(imgData);
      } catch (e) {
      } finally {
        setIsGenerating(false);
      }
    }

    if (!imgData || !imgData.url) {
      showToast(lang === 'en' ? 'Unable to download image' : 'इमेज डाऊनलोड करता आली नाही');
      return;
    }

    const a = document.createElement('a');
    a.href = imgData.url;
    a.download = `daily-panchang-${new Date().toISOString().split('T')[0]}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast(lang === 'en' ? 'Panchang image downloaded!' : 'पंचांग इमेज डाऊनलोड झाली!');
  };

  const handleCopyImageToClipboard = async () => {
    let imgData = generatedImage;
    if (!imgData && panchang) {
      setIsGenerating(true);
      try {
        imgData = await generateWithTimeout(panchang, 10000);
        if (imgData) setGeneratedImage(imgData);
      } catch (e) {
      } finally {
        setIsGenerating(false);
      }
    }

    if (imgData && imgData.blob && navigator.clipboard && navigator.clipboard.write) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': imgData.blob })
        ]);
        showToast(lang === 'en' ? 'Watermarked image copied! Paste in WhatsApp' : 'वॉटरमार्कसह इमेज कॉपी झाली! व्हॉट्सॲपवर पेस्ट करा');
        return;
      } catch (e) {}
    }

    handleCopyLink();
  };

  const handleCopyLink = () => {
    const link = 'https://dindoripranit.org';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        showToast(lang === 'en' ? 'Link copied to clipboard!' : 'लिंक कॉपी झाली!');
      }).catch(() => {
        showToast(lang === 'en' ? 'Link copied!' : 'लिंक कॉपी झाली!');
      });
    } else {
      showToast(lang === 'en' ? 'Link: https://dindoripranit.org' : 'लिंक: https://dindoripranit.org');
    }
  };

  return (
    <>
      <div className="panchang-card" onClick={() => setIsOpen(true)}>
        <div className="panchang-icon">
          <span className="calendar-month">{dateInfo.month[lang]}</span>
          <span className="calendar-day">{lang === 'mr' ? toDevanagariNumber(dateInfo.day) : dateInfo.day}</span>
        </div>
        <div className="panchang-card-info">
          <strong>{lang === 'en' ? 'Daily Panchang' : 'दैनिक पंचांग'}</strong>
          <p>{lang === 'en' ? 'Click to view details' : 'तपशील पाहण्यासाठी क्लिक करा'}</p>
        </div>
        <button
          className="card-share-btn"
          onClick={(e) => { e.stopPropagation(); handleShareClick(); }}
          title={lang === 'en' ? 'Share Panchang' : 'पंचांग शेअर करा'}
          aria-label={lang === 'en' ? 'Share Panchang' : 'पंचांग शेअर करा'}
        >
          <IoShareSocial />
        </button>
      </div>

      {isOpen && (
        <div className="panchang-modal-backdrop" onClick={() => setIsOpen(false)}>
          <div className="panchang-modal" onClick={(e) => e.stopPropagation()}>
            <div className="panchang-modal-image-header">
              <img loading="eager" decoding="async" src={headerImage} alt={lang === 'en' ? 'Daily Panchang Header' : 'दैनिक पंचांग हेडर'} />
              <div className="panchang-modal-header-actions">
                <button
                  className={`panchang-header-share-btn ${isGenerating ? 'loading' : ''}`}
                  onClick={handleShareClick}
                  disabled={isGenerating}
                  title={lang === 'en' ? 'Share Panchang' : 'पंचांग शेअर करा'}
                  aria-label={lang === 'en' ? 'Share Panchang' : 'पंचांग शेअर करा'}
                >
                  <IoShareSocial />
                </button>
                <button
                  className="close-btn"
                  onClick={() => setIsOpen(false)}
                  title={lang === 'en' ? 'Close' : 'बंद करा'}
                  aria-label={lang === 'en' ? 'Close' : 'बंद करा'}
                >
                  <IoClose />
                </button>
              </div>
            </div>

            <div className="panchang-content" ref={contentRef}>
              {loading && <p className="loading-state">{lang === 'en' ? 'Loading Panchang...' : 'पंचांग लोड होत आहे...'}</p>}
              {error && <p className="error-state">{lang === 'en' ? 'Error loading panchang data.' : 'पंचांग डेटा लोड करताना त्रुटी आली.'}</p>}
              {!loading && !error && !panchang && (
                <p>{labels.noData.en} / {labels.noData.mr}</p>
              )}
              {!loading && !error && panchang && (
                <table>
                  <tbody>
                    <tr>
                      <td>{labels.date.en} ({labels.date.mr})</td>
                      <td>{formatDualLanguageDate(panchang.date)}</td>
                    </tr>
                    <tr>
                      <td>{labels.savastar.en} ({labels.savastar.mr})</td>
                      <td>{formatValue(panchang.savastar)}</td>
                    </tr>
                    <tr>
                      <td>{labels.shake.en} ({labels.shake.mr})</td>
                      <td>{formatValue(panchang.shake)}</td>
                    </tr>
                    <tr>
                      <td>{labels.vaar.en} ({labels.vaar.mr})</td>
                      <td>{formatValue(panchang.vaar)}</td>
                    </tr>
                    <tr>
                      <td>{labels.ayan.en} ({labels.ayan.mr})</td>
                      <td>{formatValue(panchang.ayan)}</td>
                    </tr>
                    <tr>
                      <td>{labels.ritu.en} ({labels.ritu.mr})</td>
                      <td>{formatValue(panchang.ritu)}</td>
                    </tr>
                    <tr>
                      <td>{labels.maas.en} ({labels.maas.mr})</td>
                      <td>{formatValue(panchang.maas)}</td>
                    </tr>
                    <tr>
                      <td>{labels.paksha.en} ({labels.paksha.mr})</td>
                      <td>{formatValue(panchang.paksha)}</td>
                    </tr>
                    <tr>
                      <td>{labels.tithi.en} ({labels.tithi.mr})</td>
                      <td>{formatWithEndTime(panchang.tithi, panchang.tithiEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.nakshatra.en} ({labels.nakshatra.mr})</td>
                      <td>{formatWithEndTime(panchang.nakshatra, panchang.nakshatraEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.yog.en} ({labels.yog.mr})</td>
                      <td>{formatWithEndTime(panchang.yog, panchang.yogEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.karan.en} ({labels.karan.mr})</td>
                      <td>{formatWithEndTime(panchang.karan, panchang.karanEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.chandra_rashi.en} ({labels.chandra_rashi.mr})</td>
                      <td>{formatValue(panchang.chandra_Rashi)}</td>
                    </tr>
                    <tr>
                      <td>{labels.surya_rashi.en} ({labels.surya_rashi.mr})</td>
                      <td>{formatValue(panchang.surya_Rashi)}</td>
                    </tr>
                    <tr>
                      <td>{labels.sunrise.en} ({labels.sunrise.mr})</td>
                      <td>{formatTime12Hr(panchang.sunrise)}</td>
                    </tr>
                    <tr>
                      <td>{labels.sunset.en} ({labels.sunset.mr})</td>
                      <td>{formatTime12Hr(panchang.sunset)}</td>
                    </tr>
                    <tr>
                      <td>{labels.moonrise.en} ({labels.moonrise.mr})</td>
                      <td>{formatTime12Hr(panchang.moonrise)}</td>
                    </tr>
                    <tr>
                      <td>{labels.moonset.en} ({labels.moonset.mr})</td>
                      <td>{formatTime12Hr(panchang.moonset)}</td>
                    </tr>
                    <tr>
                      <td>{labels.rahukaal.en} ({labels.rahukaal.mr})</td>
                      <td>{formatRahukaal(panchang.rahukaal)}</td>
                    </tr>
                    {panchang.guruwani && (
                      <tr>
                        <td colSpan="2" style={{ fontStyle: 'italic', paddingTop: '12px' }}>
                          <strong>{labels.guruwani.en} ({labels.guruwani.mr}):</strong><br />
                          {formatValue(panchang.guruwani)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div className="panchang-modal-image-footer">
              <img loading="eager" decoding="async" src={footerImage} alt={lang === 'en' ? 'Daily Panchang Footer' : 'दैनिक पंचांग फूटेर'} />
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="share-loading-overlay">
          <div className="share-loading-card">
            <div className="share-spinner"></div>
            <p>{lang === 'en' ? 'Generating Panchang Image...' : 'पंचांग इमेज तयार होत आहे...'}</p>
          </div>
        </div>
      )}

      {/* Share Options Dialog / Action Sheet */}
      {shareOptionsOpen && (
        <div className="share-sheet-backdrop" onClick={() => setShareOptionsOpen(false)}>
          <div className="share-sheet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-sheet-header">
              <h3>{lang === 'en' ? 'Share Panchang' : 'पंचांग शेअर करा'}</h3>
              <button className="share-sheet-close" onClick={() => setShareOptionsOpen(false)}>
                <IoClose />
              </button>
            </div>

            {generatedImage && generatedImage.url && (
              <div className="share-image-preview">
                <img src={generatedImage.url} alt="Panchang Card Preview" />
              </div>
            )}

            <div className="share-options-grid">
              <button className="share-option-btn whatsapp" onClick={handleWhatsAppShare}>
                <div className="share-icon-wrapper wa"><IoLogoWhatsapp /></div>
                <span>{lang === 'en' ? 'WhatsApp' : 'व्हॉट्सॲप'}</span>
              </button>

              <button className="share-option-btn native" onClick={handleShareImageNative}>
                <div className="share-icon-wrapper sys"><IoShareSocial /></div>
                <span>{lang === 'en' ? 'Share Image' : 'इमेज शेअर करा'}</span>
              </button>

              <button className="share-option-btn download" onClick={handleDownloadImage}>
                <div className="share-icon-wrapper dl"><IoDownload /></div>
                <span>{lang === 'en' ? 'Download Image' : 'इमेज डाऊनलोड'}</span>
              </button>

              <button className="share-option-btn copy" onClick={handleCopyImageToClipboard}>
                <div className="share-icon-wrapper cp"><IoImage /></div>
                <span>{lang === 'en' ? 'Copy Image' : 'इमेज कॉपी करा'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="panchang-toast">
          <IoCheckmarkCircle className="toast-icon" />
          <span>{toastMessage}</span>
        </div>
      )}
    </>
  );
}